import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ClassRecordMemoNoteEditorHandle } from '../components/program/ClassRecordMemoNoteEditor';
import { weeklyBehaviorRecordKey } from '../components/dashboard/DashboardWeeklyPlanner';
import { useSealedMemoTagLabels } from './useSealedMemoTagLabels';
import { useAnnualBehaviorNotesQuery } from '../query/hooks/useAnnualBehaviorNotesQuery';
import {
  readProgramFixedRowsCacheOrQuery,
} from '../query/readSchoolQueryCache';
import { upsertProgramFixedContentRows } from '../services/programFixedContentService';
import { saveScopedRecordFieldsAtPosition } from '../utils/scopedRecordSave';
import {
  deleteAnnualBehaviorNoteByNaturalKey,
  upsertAnnualBehaviorNote,
} from '../services/annualBehaviorNotesService';
import { getClassMemberGradeLayoutsMap, getClassMembers } from '../services/classMembersService';
import type { AnnualBehaviorNote, ProgramScopeType } from '../types/domain';
import { annualSlotHasTimetableProgramAssignment } from '../utils/annualProgressSlotSource';
import { clearProgramLessonRecordCaches } from '../utils/annualProgressClientCacheInvalidation';
import { emitAnnualBehaviorNotesSaved } from '../utils/annualBehaviorNotesSavedEvents';
import {
  buildDefaultEvalMemberTagsNote,
  evalSerializedNoteHasNonTagText,
} from '../utils/classRecordMemoHashtags';
import type { ClassMemberGradeLayout, ClassMemberRow } from '../utils/classMembersStorage';
import {
  firstRecordGroupId,
  membersForRecordGroup,
  matchMembersByClassRecordTagKeyword,
  resolveDefaultRecordGroupId,
  type MemberWithGroupContext,
} from '../utils/classMemberRecordGroupUtils';
import { loadSelectedClassGroupId } from '../utils/selectedClassGroupPreference';
import type { DashboardAnnualDaySlot } from '../utils/dashboardAnnualDaySlots';
import {
  parseMemoFieldStorageDeep,
  serializeClassRecordCellStorage,
} from '../utils/programCellDisplayText';
import { emitProgramProgressSaved } from '../utils/programProgressSavedEvents';

function isLessonSlotNoLesson(slot: DashboardAnnualDaySlot): boolean {
  if (slot.timetableSlotKind === 'out_of_term' || slot.timetableSlotKind === 'holiday') return true;
  return !annualSlotHasTimetableProgramAssignment(slot.timetableText, slot.timetableColorHex);
}

function lessonContextLine(slot: DashboardAnnualDaySlot): string {
  const [, m, d] = slot.dateIso.split('-');
  const dParsed = new Date(`${slot.dateIso}T00:00:00`);
  const dow = (['일', '월', '화', '수', '목', '금', '토'] as const)[dParsed.getDay()];
  return `${Number(m)}월${Number(d)}일 (${dow}) ${slot.periodLabel} ${slot.timetableText || slot.templateLabel}`;
}

function behaviorContextLine(slot: DashboardAnnualDaySlot): string {
  const [, m, d] = slot.dateIso.split('-');
  const dParsed = new Date(`${slot.dateIso}T00:00:00`);
  const dow = (['일', '월', '화', '수', '목', '금', '토'] as const)[dParsed.getDay()];
  return `${Number(m)}월${Number(d)}일 (${dow}) ${slot.periodLabel}`;
}

export function useDashboardLessonSlotRecords(params: {
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
}) {
  const { workspaceId, gradeId, academicYear, termNumber } = params;
  const [selectedSlot, setSelectedSlot] = useState<DashboardAnnualDaySlot | null>(null);
  const [memberGradeLayout, setMemberGradeLayout] = useState<ClassMemberGradeLayout>({
    groups: [],
    membersByGroupId: {},
  });
  const [classMembers, setClassMembers] = useState<ClassMemberRow[]>([]);
  const [lessonRecordModalOpen, setLessonRecordModalOpen] = useState(false);
  const [lessonRecordNote, setLessonRecordNote] = useState('');
  const [lessonRecordTab, setLessonRecordTab] = useState<'lesson' | 'eval_log'>('lesson');
  const [lessonRecordNoteEval, setLessonRecordNoteEval] = useState('');
  const [lessonRecordMemberGroupId, setLessonRecordMemberGroupId] = useState<string | null>(null);
  const [lessonRecordEvalMemoMemberGroupId, setLessonRecordEvalMemoMemberGroupId] = useState<string | null>(
    null
  );
  const [lessonEvalTagGenConfirm, setLessonEvalTagGenConfirm] = useState<{
    nextNote: string;
    sourceGroupId: string;
  } | null>(null);
  const [lessonRecordModalExpanded, setLessonRecordModalExpanded] = useState(false);
  const [lessonRecordSaving, setLessonRecordSaving] = useState(false);
  const [lessonRecordError, setLessonRecordError] = useState<string | null>(null);
  const lessonRecordMemoEditorRef = useRef<ClassRecordMemoNoteEditorHandle | null>(null);
  const lessonRecordEvalEditorRef = useRef<ClassRecordMemoNoteEditorHandle | null>(null);
  const [lessonTagGroupPick, setLessonTagGroupPick] = useState<{
    keyword: string;
    candidates: MemberWithGroupContext[];
  } | null>(null);
  const [behaviorNoteModalOpen, setBehaviorNoteModalOpen] = useState(false);
  const [behaviorNote, setBehaviorNote] = useState('');
  const [behaviorNoteMemberGroupId, setBehaviorNoteMemberGroupId] = useState<string | null>(null);
  const [behaviorNoteModalExpanded, setBehaviorNoteModalExpanded] = useState(false);
  const [behaviorNoteSaving, setBehaviorNoteSaving] = useState(false);
  const [behaviorNoteError, setBehaviorNoteError] = useState<string | null>(null);
  const behaviorMemoEditorRef = useRef<ClassRecordMemoNoteEditorHandle | null>(null);
  const [behaviorTagGroupPick, setBehaviorTagGroupPick] = useState<{
    keyword: string;
    candidates: MemberWithGroupContext[];
  } | null>(null);
  const sealedLabelByToken = useSealedMemoTagLabels([lessonRecordNote, lessonRecordNoteEval, behaviorNote]);

  const loadMemberGradeLayout = useCallback(async () => {
    if (!workspaceId || !gradeId) return;
    try {
      const [layoutsMap, members] = await Promise.all([
        getClassMemberGradeLayoutsMap(workspaceId, [gradeId]),
        getClassMembers(workspaceId, gradeId),
      ]);
      setMemberGradeLayout(layoutsMap[gradeId] ?? { groups: [], membersByGroupId: {} });
      setClassMembers(members);
    } catch {
      /* ignore */
    }
  }, [workspaceId, gradeId]);

  useEffect(() => {
    void loadMemberGradeLayout();
  }, [loadMemberGradeLayout]);

  const behaviorNotesQuery = useAnnualBehaviorNotesQuery({
    workspaceId,
    academicYear: selectedSlot?.academicYear || academicYear,
    termNumber: (selectedSlot?.termNumber || termNumber) as 1 | 2,
    gradeId,
    enabled: !!workspaceId && !!gradeId && academicYear > 0,
  });
  const behaviorNotes = behaviorNotesQuery.data ?? [];

  const behaviorNoteByDateSlotKey = useMemo(() => {
    const map = new Map<string, AnnualBehaviorNote>();
    for (const note of behaviorNotes) {
      map.set(weeklyBehaviorRecordKey(note.lesson_date, note.day_row_order), note);
    }
    return map;
  }, [behaviorNotes]);

  const lessonRecordMembers = useMemo(
    () => membersForRecordGroup(memberGradeLayout, lessonRecordMemberGroupId, classMembers),
    [memberGradeLayout, lessonRecordMemberGroupId, classMembers]
  );
  const behaviorNoteMembers = useMemo(
    () => membersForRecordGroup(memberGradeLayout, behaviorNoteMemberGroupId, classMembers),
    [memberGradeLayout, behaviorNoteMemberGroupId, classMembers]
  );

  const finalizeLessonHashtagForMemberGroup = useCallback(
    (tagBody: string) => {
      const hits = matchMembersByClassRecordTagKeyword(memberGradeLayout, tagBody);
      if (hits.length === 0) return;
      if (hits.length === 1) {
        setLessonRecordMemberGroupId(hits[0].groupId);
        return;
      }
      setLessonTagGroupPick({ keyword: tagBody, candidates: hits });
    },
    [memberGradeLayout]
  );

  const finalizeBehaviorHashtagForMemberGroup = useCallback(
    (tagBody: string) => {
      const hits = matchMembersByClassRecordTagKeyword(memberGradeLayout, tagBody);
      if (hits.length === 0) return;
      if (hits.length === 1) {
        setBehaviorNoteMemberGroupId(hits[0].groupId);
        return;
      }
      setBehaviorTagGroupPick({ keyword: tagBody, candidates: hits });
    },
    [memberGradeLayout]
  );

  const lessonRecordContextLine = selectedSlot ? lessonContextLine(selectedSlot) : '';
  const lessonRecordUnitLine = selectedSlot?.display.progressUnit ?? '';
  const behaviorNoteContextLine = selectedSlot ? behaviorContextLine(selectedSlot) : '';

  const openLessonRecord = useCallback((slot: DashboardAnnualDaySlot) => {
    if (isLessonSlotNoLesson(slot)) return;
    setSelectedSlot(slot);
    const d = slot.display;
    const tabKind = d.progressClassRecordKind === 'eval_log' ? 'eval_log' : 'lesson';
    const storedNote = d.progressClassRecordNote ?? '';
    setLessonRecordTab(tabKind);
    setLessonRecordNote(tabKind === 'lesson' ? storedNote : '');
    setLessonRecordNoteEval(tabKind === 'eval_log' ? storedNote : '');
    const savedGroupId = d.progressRecordMemberGroupId ?? null;
    const defaultGroupId =
      savedGroupId && memberGradeLayout.groups.some((g) => g.id === savedGroupId)
        ? savedGroupId
        : resolveDefaultRecordGroupId(
            memberGradeLayout,
            loadSelectedClassGroupId(workspaceId, gradeId)
          );
    setLessonRecordMemberGroupId(defaultGroupId);
    setLessonRecordEvalMemoMemberGroupId(
      tabKind === 'eval_log' && storedNote.trim() ? defaultGroupId : null
    );
    setLessonEvalTagGenConfirm(null);
    setLessonRecordModalExpanded(false);
    setLessonRecordError(null);
    setLessonRecordModalOpen(true);
  }, [memberGradeLayout, workspaceId, gradeId]);

  const readLessonRecordNote = useCallback(() => {
    if (lessonRecordTab === 'eval_log') {
      const domNote = lessonRecordEvalEditorRef.current?.readSerializedNoteFromDom?.();
      return domNote !== undefined ? domNote : lessonRecordNoteEval;
    }
    const domNote = lessonRecordMemoEditorRef.current?.readSerializedNoteFromDom?.();
    return domNote !== undefined ? domNote : lessonRecordNote;
  }, [lessonRecordTab, lessonRecordNote, lessonRecordNoteEval]);

  const applyLessonMemberTags = useCallback((nextNote: string, sourceGroupId: string) => {
    if (lessonRecordTab === 'eval_log') {
      setLessonRecordNoteEval(nextNote);
      setLessonRecordEvalMemoMemberGroupId(sourceGroupId);
    } else {
      setLessonRecordNote(nextNote);
    }
    setLessonRecordMemberGroupId(sourceGroupId);
    setLessonRecordError(null);
  }, [lessonRecordTab]);

  const handleGenerateLessonMemberTags = useCallback(() => {
    const gid = (lessonRecordMemberGroupId ?? '').trim();
    if (!gid) {
      setLessonRecordError('멤버 그룹을 선택한 뒤 멤버 태그 생성을 눌러 주세요.');
      return;
    }
    const members = membersForRecordGroup(memberGradeLayout, gid, classMembers);
    if (members.length === 0) return;
    const nextNote = buildDefaultEvalMemberTagsNote(members);
    const current =
      lessonRecordTab === 'eval_log'
        ? (lessonRecordEvalEditorRef.current?.readSerializedNoteFromDom?.() ?? lessonRecordNoteEval)
        : (lessonRecordMemoEditorRef.current?.readSerializedNoteFromDom?.() ?? lessonRecordNote);
    if (evalSerializedNoteHasNonTagText(current)) {
      setLessonEvalTagGenConfirm({ nextNote, sourceGroupId: gid });
      return;
    }
    applyLessonMemberTags(nextNote, gid);
  }, [
    applyLessonMemberTags,
    classMembers,
    lessonRecordMemberGroupId,
    lessonRecordNote,
    lessonRecordNoteEval,
    lessonRecordTab,
    memberGradeLayout,
  ]);

  const lessonRecordCanDelete = useMemo(() => {
    if (!selectedSlot) return false;
    const stored = parseMemoFieldStorageDeep(selectedSlot.recordClassTextRaw ?? '');
    const storedText = stored.text.trim();
    if (stored.note.trim()) return true;
    if (storedText === 'lesson' || storedText === 'eval_log') return true;
    if (lessonRecordNote.trim() || lessonRecordNoteEval.trim()) return true;
    return lessonRecordTab === 'lesson' || lessonRecordTab === 'eval_log';
  }, [lessonRecordNote, lessonRecordNoteEval, lessonRecordTab, selectedSlot]);

  const saveLessonRecord = useCallback(
    async (opts?: { delete?: boolean; aiFeedbackText?: string }): Promise<boolean> => {
      if (!workspaceId || !gradeId || !selectedSlot) return false;
      setLessonRecordSaving(true);
      setLessonRecordError(null);
      try {
        const preserved = parseMemoFieldStorageDeep(selectedSlot.recordClassTextRaw ?? '');
        const preservedBehavior = preserved.behaviorNote;
        const recordClassText = opts?.delete
          ? serializeClassRecordCellStorage('', '', preservedBehavior)
          : serializeClassRecordCellStorage(
              lessonRecordTab,
              readLessonRecordNote().trim(),
              preservedBehavior,
              preserved.assignmentMemoItemId,
              preserved.assignmentMemoItemTitle,
              preserved.assignmentMemoGroupId,
              preserved.assignmentMemoGroupTitle,
              {
                ...(preserved.aiFeedback ?? {}),
                ...(opts?.aiFeedbackText !== undefined ? { [lessonRecordTab]: opts.aiFeedbackText } : {}),
              }
            );
        const recordMemberGroupId = opts?.delete ? null : lessonRecordMemberGroupId;
        let savedScopedScope: { scopeType: ProgramScopeType; scopeKey: string } | null = null;

        if (selectedSlot.fixedContentRowId) {
          const fixedCache = readProgramFixedRowsCacheOrQuery(
            workspaceId,
            selectedSlot.academicYear,
            selectedSlot.termNumber,
            gradeId
          );
          const existingRow = fixedCache.found
            ? fixedCache.rows.find((r) => r.id === selectedSlot.fixedContentRowId)
            : null;
          if (existingRow) {
            await upsertProgramFixedContentRows([
              {
                id: existingRow.id,
                workspace_id: workspaceId,
                grade_id: gradeId,
                academic_year: selectedSlot.academicYear,
                term_number: selectedSlot.termNumber,
                event_name: existingRow.event_name,
                order_index: existingRow.order_index,
                source_annual_cell_key: existingRow.source_annual_cell_key,
                timetable_text: existingRow.timetable_text,
                unit_text: existingRow.unit_text,
                learning_text: existingRow.learning_text,
                page_text: existingRow.page_text,
                assistant_page_text: existingRow.assistant_page_text,
                current_period_text: existingRow.current_period_text,
                total_period_text: existingRow.total_period_text,
                material_text: existingRow.material_text,
                record_cross_curricular_text: existingRow.record_cross_curricular_text,
                record_memo_text: existingRow.record_memo_text,
                record_evaluation_text: existingRow.record_evaluation_text,
                record_class_text: recordClassText,
                record_member_group_id: recordMemberGroupId,
                bg_color_by_field: existingRow.bg_color_by_field ?? {},
              },
            ]);
          }
        } else if (selectedSlot.scopedContentScopeKey && selectedSlot.scopedContentScopeType) {
          const targetScopeKey = selectedSlot.scopedContentScopeKey;
          const targetScopeType = selectedSlot.scopedContentScopeType as ProgramScopeType;
          /** 행 id로 기록 칸만 고친다. 행이 없으면 스코프 안 위치에 (중복 없이) 만든 뒤 고친다 */
          await saveScopedRecordFieldsAtPosition(
            {
              workspaceId,
              academicYear: selectedSlot.academicYear,
              termNumber: selectedSlot.termNumber,
              gradeId,
              scopeType: targetScopeType,
              scopeKey: targetScopeKey,
            },
            { rowId: selectedSlot.scopedContentRowId, position: selectedSlot.scopedContentOrderIndex },
            { record_class_text: recordClassText, record_member_group_id: recordMemberGroupId }
          );
          savedScopedScope = { scopeType: targetScopeType, scopeKey: targetScopeKey };
        } else {
          throw new Error('해당 수업의 프로그램 데이터가 없습니다. 프로그램 페이지에서 먼저 데이터를 생성해주세요.');
        }

        if (selectedSlot.fixedContentRowId) {
          clearProgramLessonRecordCaches(
            workspaceId,
            selectedSlot.academicYear,
            selectedSlot.termNumber,
            gradeId,
            { type: 'fixed' }
          );
        } else if (savedScopedScope) {
          clearProgramLessonRecordCaches(
            workspaceId,
            selectedSlot.academicYear,
            selectedSlot.termNumber,
            gradeId,
            {
              type: 'scoped',
              scopeType: savedScopedScope.scopeType,
              scopeKey: savedScopedScope.scopeKey,
            }
          );
        }

        setLessonRecordModalOpen(false);
        emitProgramProgressSaved({
          workspaceId,
          academicYear: selectedSlot.academicYear,
          termNumber: selectedSlot.termNumber,
          gradeId,
        });
        return true;
      } catch (e) {
        setLessonRecordError(
          e instanceof Error ? e.message : opts?.delete ? '삭제에 실패했습니다.' : '저장에 실패했습니다.'
        );
        return false;
      } finally {
        setLessonRecordSaving(false);
      }
    },
    [workspaceId, gradeId, selectedSlot, lessonRecordTab, lessonRecordMemberGroupId, readLessonRecordNote]
  );

  const openBehaviorRecord = useCallback(
    (slot: DashboardAnnualDaySlot) => {
      setSelectedSlot(slot);
      const key = weeklyBehaviorRecordKey(slot.dateIso, slot.slotIndex);
      const existing = behaviorNoteByDateSlotKey.get(key);
      setBehaviorNote(existing?.behavior_note ?? '');
      const savedGroupId = existing?.record_member_group_id ?? null;
      setBehaviorNoteMemberGroupId(
        savedGroupId && memberGradeLayout.groups.some((g) => g.id === savedGroupId)
          ? savedGroupId
          : resolveDefaultRecordGroupId(
              memberGradeLayout,
              loadSelectedClassGroupId(workspaceId, gradeId)
            )
      );
      setBehaviorNoteModalExpanded(false);
      setBehaviorNoteError(null);
      setBehaviorNoteModalOpen(true);
    },
    [behaviorNoteByDateSlotKey, memberGradeLayout, workspaceId, gradeId]
  );

  const readBehaviorNote = useCallback(() => {
    const domNote = behaviorMemoEditorRef.current?.readSerializedNoteFromDom?.();
    return domNote !== undefined ? domNote : behaviorNote;
  }, [behaviorNote]);

  const saveBehaviorNote = useCallback(async (aiFeedback?: string): Promise<boolean> => {
    if (!workspaceId || !gradeId || !selectedSlot) return false;
    setBehaviorNoteSaving(true);
    setBehaviorNoteError(null);
    try {
      await upsertAnnualBehaviorNote({
        workspace_id: workspaceId,
        grade_id: gradeId,
        academic_year: selectedSlot.academicYear,
        term_number: selectedSlot.termNumber,
        lesson_date: selectedSlot.dateIso,
        day_row_order: selectedSlot.slotIndex,
        behavior_note: readBehaviorNote().trim(),
        record_member_group_id: behaviorNoteMemberGroupId,
        source_annual_cell_key_snapshot: selectedSlot.annualCellKey,
        period_label_snapshot: selectedSlot.periodLabel,
        timetable_text_snapshot: selectedSlot.timetableText,
        ...(aiFeedback !== undefined ? { ai_feedback: aiFeedback } : {}),
      });
      emitAnnualBehaviorNotesSaved({
        workspaceId,
        academicYear: selectedSlot.academicYear,
        termNumber: selectedSlot.termNumber,
        gradeId,
      });
      setBehaviorNoteModalOpen(false);
      return true;
    } catch (e) {
      setBehaviorNoteError(e instanceof Error ? e.message : '저장에 실패했습니다.');
      return false;
    } finally {
      setBehaviorNoteSaving(false);
    }
  }, [workspaceId, gradeId, selectedSlot, behaviorNoteMemberGroupId, readBehaviorNote]);

  /** 선택 슬롯 행동특성 기록에 내장된 AI 피드백 */
  const behaviorNoteAiFeedback = useMemo(() => {
    if (!selectedSlot) return null;
    return (
      behaviorNoteByDateSlotKey.get(weeklyBehaviorRecordKey(selectedSlot.dateIso, selectedSlot.slotIndex))
        ?.ai_feedback ?? null
    );
  }, [behaviorNoteByDateSlotKey, selectedSlot]);

  const behaviorNoteCanDelete = useMemo(() => {
    if (!selectedSlot) return false;
    const key = weeklyBehaviorRecordKey(selectedSlot.dateIso, selectedSlot.slotIndex);
    const stored = (behaviorNoteByDateSlotKey.get(key)?.behavior_note ?? '').trim();
    if (stored) return true;
    return !!behaviorNote.trim();
  }, [behaviorNote, behaviorNoteByDateSlotKey, selectedSlot]);

  const deleteBehaviorNote = useCallback(async () => {
    if (!workspaceId || !gradeId || !selectedSlot) return;
    setBehaviorNoteSaving(true);
    setBehaviorNoteError(null);
    try {
      await deleteAnnualBehaviorNoteByNaturalKey({
        workspaceId,
        gradeId,
        academicYear: selectedSlot.academicYear,
        termNumber: selectedSlot.termNumber,
        lessonDate: selectedSlot.dateIso,
        dayRowOrder: selectedSlot.slotIndex,
      });
      setBehaviorNoteModalOpen(false);
    } catch (e) {
      setBehaviorNoteError(e instanceof Error ? e.message : '삭제에 실패했습니다.');
    } finally {
      setBehaviorNoteSaving(false);
    }
  }, [workspaceId, gradeId, selectedSlot]);

  return {
    classMembers,
    workspaceId,
    selectedSlot,
    openLessonRecord,
    openBehaviorRecord,
    lessonRecordModalOpen,
    setLessonRecordModalOpen,
    lessonRecordModalExpanded,
    setLessonRecordModalExpanded,
    lessonRecordTab,
    setLessonRecordTab,
    lessonRecordNote,
    setLessonRecordNote,
    lessonRecordNoteEval,
    setLessonRecordNoteEval,
    lessonRecordMemberGroupId,
    setLessonRecordMemberGroupId,
    lessonRecordEvalMemoMemberGroupId,
    setLessonRecordEvalMemoMemberGroupId,
    lessonRecordSaving,
    lessonRecordError,
    setLessonRecordError,
    lessonRecordContextLine,
    lessonRecordUnitLine,
    lessonRecordMembers,
    lessonRecordMemoEditorRef,
    lessonRecordEvalEditorRef,
    lessonRecordCanDelete,
    saveLessonRecord,
    handleGenerateLessonMemberTags,
    finalizeLessonHashtagForMemberGroup,
    lessonTagGroupPick,
    setLessonTagGroupPick,
    lessonEvalTagGenConfirm,
    setLessonEvalTagGenConfirm,
    applyLessonMemberTags,
    behaviorNoteModalOpen,
    setBehaviorNoteModalOpen,
    behaviorNoteModalExpanded,
    setBehaviorNoteModalExpanded,
    behaviorNote,
    setBehaviorNote,
    behaviorNoteMemberGroupId,
    setBehaviorNoteMemberGroupId,
    behaviorNoteSaving,
    behaviorNoteError,
    behaviorNoteContextLine,
    behaviorNoteMembers,
    behaviorMemoEditorRef,
    behaviorNoteCanDelete,
    behaviorNoteAiFeedback,
    saveBehaviorNote,
    deleteBehaviorNote,
    finalizeBehaviorHashtagForMemberGroup,
    behaviorTagGroupPick,
    setBehaviorTagGroupPick,
    memberGradeLayout,
    sealedLabelByToken,
    firstRecordGroupId: firstRecordGroupId(memberGradeLayout),
  };
}
